import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Pagination,
  Tooltip,
  Alert,
  CircularProgress,
  InputAdornment,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Star as StarIcon,
  Business as BusinessIcon,
  Assignment as AssignmentIcon,
  GpsFixed as GpsFixedIcon,
  CloudUpload as CloudUploadIcon,
  Image as ImageIcon,
  Info as InfoIcon,
  PersonAdd as PersonAddIcon
} from '@mui/icons-material';
import { adminAPI } from '../../services/api';
import { useNotification } from '../../context/NotificationContext';
import ImageManager from '../../components/ImageManager';

const SalonManagement = () => {
  const [salons, setSalons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [verifiedFilter, setVerifiedFilter] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('add'); // 'add', 'edit', 'view'
  const [selectedSalon, setSelectedSalon] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    email: '',
    phone: '',
    website: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      coordinates: {
        type: 'Point',
        coordinates: [0, 0]
      }
    },
    priceRange: 'moderate',
    isActive: true,
    isVerified: false,
    featured: false,
    image: null
  });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [salonToDelete, setSalonToDelete] = useState(null);
  
  // Service assignment states
  const [serviceAssignmentOpen, setServiceAssignmentOpen] = useState(false);
  const [selectedSalonForServices, setSelectedSalonForServices] = useState(null);
  const [availableServices, setAvailableServices] = useState([]);
  const [assignedServices, setAssignedServices] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [customPrices, setCustomPrices] = useState({}); // New state for custom prices
  const [fetchingCurrentLocation, setFetchingCurrentLocation] = useState(false); // New state for current location fetching
  const [dialogTab, setDialogTab] = useState(0); // Tab state for dialog

  // Owner assignment states
  const [ownerAssignmentOpen, setOwnerAssignmentOpen] = useState(false);
  const [selectedSalonForOwner, setSelectedSalonForOwner] = useState(null);
  const [availableOwners, setAvailableOwners] = useState([]);
  const [selectedOwner, setSelectedOwner] = useState(''); // Keep as empty string for controlled component
  const [ownerSearchTerm, setOwnerSearchTerm] = useState('');
  const [ownersLoading, setOwnersLoading] = useState(false);

  const { showNotification } = useNotification();

  useEffect(() => {
    fetchSalons();
  }, [currentPage, searchTerm, statusFilter, verifiedFilter]);

  // Function to get current location coordinates
  const getCurrentLocation = async () => {
    if (!navigator.geolocation) {
      showNotification('Geolocation is not supported by this browser', 'error');
      return;
    }

    try {
      setFetchingCurrentLocation(true);
      
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          resolve,
          reject,
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 60000
          }
        );
      });

      const { latitude, longitude } = position.coords;
      
      setFormData({
        ...formData,
        address: {
          ...formData.address,
          coordinates: {
            ...formData.address.coordinates,
            coordinates: [longitude, latitude]
          }
        }
      });
      
      showNotification('Current location coordinates fetched successfully!', 'success');
    } catch (error) {
      console.error('Error getting current location:', error);
      let errorMessage = 'Failed to get current location. ';
      
      switch (error.code) {
        case error.PERMISSION_DENIED:
          errorMessage += 'Location access denied by user.';
          break;
        case error.POSITION_UNAVAILABLE:
          errorMessage += 'Location information unavailable.';
          break;
        case error.TIMEOUT:
          errorMessage += 'Location request timed out.';
          break;
        default:
          errorMessage += 'Please try again.';
          break;
      }
      
      showNotification(errorMessage, 'error');
    } finally {
      setFetchingCurrentLocation(false);
    }
  };
  const fetchSalons = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 10,
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter && { status: statusFilter }),
        ...(verifiedFilter && { verified: verifiedFilter })
      };

      const response = await adminAPI.getSalons(params);
      setSalons(response.data.salons);
      setTotalPages(response.data.pagination.totalPages);
    } catch (error) {
      showNotification('Failed to fetch salons', 'error');
      console.error('Error fetching salons:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = async (mode, salon = null) => {
    setDialogMode(mode);
    setSelectedSalon(salon);
    console.log('Opening dialog with mode:', mode, 'and salon:', salon);
    
    if (mode === 'add') {
      setFormData({
        name: '',
        description: '',
        email: '',
        phone: '',
        website: '',
        address: {
          street: '',
          city: '',
          state: '',
          zipCode: '',
          coordinates: {
            type: 'Point',
            coordinates: [0, 0]
          }
        },
        priceRange: 'moderate',
        isActive: true,
        isVerified: false,
        featured: false
      });
      setOpenDialog(true);
    } else if ((mode === 'edit' || mode === 'view') && salon) {
      try {
        // Fetch complete salon data for editing or viewing
        const response = await adminAPI.getSalon(salon.id);
        const salonData = response.data.salon;
        
        setFormData({
          name: salonData.name || '',
          description: salonData.description || '',
          email: salonData.email || '',
          phone: salonData.phone || '',
          website: salonData.website || '',
          address: salonData.address || {
            street: '',
            city: '',
            state: '',
            zipCode: '',
            coordinates: {
              type: 'Point',
              coordinates: [0, 0]
            }
          },
          priceRange: salonData.priceRange || 'moderate',
          isActive: salonData.isActive !== undefined ? salonData.isActive : true,
          isVerified: salonData.isVerified !== undefined ? salonData.isVerified : false,
          featured: salonData.featured !== undefined ? salonData.featured : false
        });
        setOpenDialog(true);
      } catch (error) {
        showNotification('Failed to load salon details', 'error');
        console.error('Error loading salon details:', error);
      }
    } else {
      setOpenDialog(true);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedSalon(null);
    setDialogTab(0); // Reset tab to first tab
    setFormData({
      name: '',
      description: '',
      email: '',
      phone: '',
      website: '',
      address: {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        coordinates: {
          type: 'Point',
          coordinates: [0, 0]
        }
      },
      priceRange: 'moderate',
      isActive: true,
      isVerified: false,
      featured: false
    });
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const buildFormData = () => {
    const fd = new FormData();
    Object.keys(formData).forEach(key => {
      if (key === 'image' && formData[key]) {
        fd.append('image', formData[key]);
      } else if (key === 'address') {
        fd.append('address[street]', formData.address.street || '');
        fd.append('address[city]', formData.address.city || '');
        fd.append('address[state]', formData.address.state || '');
        fd.append('address[zipCode]', formData.address.zipCode || '');
        fd.append('address[coordinates][type]', 'Point');
        fd.append('address[coordinates][coordinates][0]', formData.address.coordinates?.coordinates?.[0] || 0);
        fd.append('address[coordinates][coordinates][1]', formData.address.coordinates?.coordinates?.[1] || 0);
      } else if (typeof formData[key] === 'boolean') {
        fd.append(key, formData[key].toString());
      } else if (key !== 'image') {
        fd.append(key, formData[key] || '');
      }
    });
    return fd;
  };

  const handleSubmit = async () => {
    try {
      const submitData = buildFormData();

      if (dialogMode === 'add') {
        await adminAPI.createSalon(submitData);
        showNotification('Salon created successfully', 'success');
      } else if (dialogMode === 'edit') {
        await adminAPI.updateSalon(selectedSalon.id, submitData);
        showNotification('Salon updated successfully', 'success');
      }
      
      handleCloseDialog();
      fetchSalons();
    } catch (error) {
      showNotification(
        error.response?.data?.message || 'Operation failed',
        'error'
      );
      console.error('Error saving salon:', error);
    }
  };

  const handleDelete = async () => {
    try {
      await adminAPI.deleteSalon(salonToDelete.id);
      showNotification('Salon deleted successfully', 'success');
      setDeleteConfirmOpen(false);
      setSalonToDelete(null);
      fetchSalons();
    } catch (error) {
      showNotification(
        error.response?.data?.message || 'Failed to delete salon',
        'error'
      );
      console.error('Error deleting salon:', error);
    }
  };

  const handleToggleStatus = async (salon) => {
    try {
      await adminAPI.toggleSalonStatus(salon.id, !salon.isActive);
      showNotification(
        `Salon ${!salon.isActive ? 'activated' : 'deactivated'} successfully`,
        'success'
      );
      fetchSalons();
    } catch (error) {
      showNotification('Failed to update salon status', 'error');
      console.error('Error updating salon status:', error);
    }
  };

  // Service assignment functions
  const handleOpenServiceAssignment = async (salon) => {
    setSelectedSalonForServices(salon);
    setServiceAssignmentOpen(true);
    setServicesLoading(true);
    
    try {
      // Fetch available services (excluding already assigned ones)
      const unassignedResponse = await adminAPI.getUnassignedServices({ 
        excludeSalonId: salon.id 
      });
      setAvailableServices(unassignedResponse.data.services || []);
      
      // Fetch salon's current services
      const salonResponse = await adminAPI.getSalon(salon.id);
      setAssignedServices(salonResponse.data.salon.services || []);
      setSelectedServices([]);
    } catch (error) {
      showNotification('Failed to load services', 'error');
      console.error('Error loading services:', error);
    } finally {
      setServicesLoading(false);
    }
  };

  const handleCloseServiceAssignment = () => {
    setServiceAssignmentOpen(false);
    setSelectedSalonForServices(null);
    setAvailableServices([]);
    setAssignedServices([]);
    setSelectedServices([]);
    setCustomPrices({}); // Reset custom prices
  };

  const handleServiceSelection = (serviceId) => {
    setSelectedServices(prev => 
      prev.includes(serviceId) 
        ? prev.filter(id => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  const handleCustomPriceChange = (serviceId, price) => {
    setCustomPrices(prev => ({
      ...prev,
      [serviceId]: price
    }));
  };

  const handleAssignServices = async () => {
    if (selectedServices.length === 0) {
      showNotification('Please select at least one service to assign', 'warning');
      return;
    }

    try {
      // Prepare services with custom prices
      const servicesData = selectedServices.map(serviceId => ({
        serviceId,
        ...(customPrices[serviceId] && { customPrice: parseFloat(customPrices[serviceId]) })
      }));

      await adminAPI.assignServicesToSalon(selectedSalonForServices.id, {
        action: 'add',
        services: servicesData
      });
      
      showNotification(
        `${selectedServices.length} service(s) assigned successfully`,
        'success'
      );
      
      handleCloseServiceAssignment();
      fetchSalons();
    } catch (error) {
      showNotification(
        error.response?.data?.message || 'Failed to assign services',
        'error'
      );
      console.error('Error assigning services:', error);
    }
  };

  // Owner assignment handlers
  const handleOpenOwnerAssignment = async (salon) => {
    setSelectedSalonForOwner(salon);
    setOwnerAssignmentOpen(true);
    await loadAvailableOwners();
  };

  const loadAvailableOwners = async (search = '') => {
    try {
      setOwnersLoading(true);
      const response = await adminAPI.getAvailableOwners(search);
      console.log('🔍 Available owners API response:', response.data);
      console.log('🔍 Users array:', response.data.users);
      console.log('🔍 First user structure:', response.data.users[0]);
      setAvailableOwners(response.data.users);
    } catch (error) {
      showNotification('Failed to load available owners', 'error');
      console.error('Error loading owners:', error);
    } finally {
      setOwnersLoading(false);
    }
  };

  const handleCloseOwnerAssignment = () => {
    setOwnerAssignmentOpen(false);
    setSelectedSalonForOwner(null);
    setAvailableOwners([]);
    setSelectedOwner(''); // Ensure it's always a string, never undefined
    setOwnerSearchTerm('');
    console.log('🔄 Owner assignment dialog closed, selectedOwner reset to empty string');
  };

  const handleOwnerSearchChange = (event) => {
    const value = event.target.value;
    setOwnerSearchTerm(value);
    console.log('🔍 Search term changed:', value);
    // Debounce search
    setTimeout(() => {
      loadAvailableOwners(value);
    }, 300);
  };

  const handleAssignOwner = async () => {
    console.log('🎯 Attempting to assign owner:', selectedOwner);
    if (!selectedOwner) {
      showNotification('Please select an owner to assign', 'warning');
      return;
    }

    try {
      await adminAPI.assignSalonOwner(selectedSalonForOwner.id, selectedOwner);
      showNotification('Owner assigned successfully', 'success');
      handleCloseOwnerAssignment();
      fetchSalons(); // Refresh the salon list
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to assign owner', 'error');
      console.error('Error assigning owner:', error);
    }
  };

  const handleUnassignOwner = async () => {
    try {
      await adminAPI.unassignSalonOwner(selectedSalonForOwner.id);
      showNotification('Owner unassigned successfully', 'success');
      handleCloseOwnerAssignment();
      fetchSalons(); // Refresh the salon list
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to unassign owner', 'error');
      console.error('Error unassigning owner:', error);
    }
  };

  const handleRemoveService = async (serviceId) => {
    try {
      await adminAPI.assignServicesToSalon(selectedSalonForServices.id, {
        action: 'remove',
        services: [{ serviceId: serviceId }]
      });
      
      showNotification('Service removed successfully', 'success');
      
      // Refresh the service lists
      const unassignedResponse = await adminAPI.getUnassignedServices({ 
        excludeSalonId: selectedSalonForServices.id 
      });
      setAvailableServices(unassignedResponse.data.services || []);
      
      const salonResponse = await adminAPI.getSalon(selectedSalonForServices.id);
      setAssignedServices(salonResponse.data.salon.services || []);
      
      fetchSalons();
    } catch (error) {
      showNotification('Failed to remove service', 'error');
      console.error('Error removing service:', error);
    }
  };

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const handleFilterChange = (filterType, value) => {
    if (filterType === 'status') {
      setStatusFilter(value);
    } else if (filterType === 'verified') {
      setVerifiedFilter(value);
    }
    setCurrentPage(1);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BusinessIcon /> Salon Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog('add')}
        >
          Add New Salon
        </Button>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search salons..."
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Verification</InputLabel>
                <Select
                  value={verifiedFilter}
                  label="Verification"
                  onChange={(e) => handleFilterChange('verified', e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Verified</MenuItem>
                  <MenuItem value="false">Unverified</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('');
                  setVerifiedFilter('');
                  setCurrentPage(1);
                }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Salons Table */}
      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Contact</TableCell>
                      <TableCell>Location</TableCell>
                      <TableCell>Rating</TableCell>
                      <TableCell>Services</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Verification</TableCell>
                      <TableCell>Featured</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {salons.map((salon) => (
                      <TableRow key={salon.id}>
                        <TableCell>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {salon.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Owner: {salon.owner}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <EmailIcon fontSize="small" color="action" />
                              <Typography variant="caption">{salon.email}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <PhoneIcon fontSize="small" color="action" />
                              <Typography variant="caption">{salon.phone}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <LocationIcon fontSize="small" color="action" />
                            <Typography variant="caption">{salon.address}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <StarIcon fontSize="small" color="warning" />
                            <Typography variant="body2">
                              {salon.rating?.toFixed(1) || 'N/A'} ({salon.reviewCount || 0})
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{salon.servicesCount || 0}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={salon.isActive ? 'Active' : 'Inactive'}
                            color={salon.isActive ? 'success' : 'error'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={salon.isVerified ? 'Verified' : 'Unverified'}
                            color={salon.isVerified ? 'primary' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={salon.featured ? 'Featured' : 'Not Featured'}
                            color={salon.featured ? 'warning' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDialog('view', salon)}
                              >
                                <ViewIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit Salon">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDialog('edit', salon)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Assign Services">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenServiceAssignment(salon)}
                              >
                                <AssignmentIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Assign Owner">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleOpenOwnerAssignment(salon)}
                              >
                                <PersonAddIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Salon">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  setSalonToDelete(salon);
                                  setDeleteConfirmOpen(true);
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                />
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          {dialogMode === 'add' ? 'Add New Salon' : 
           dialogMode === 'edit' ? 'Edit Salon' : 'Salon Details'}
        </DialogTitle>
        <DialogContent>
          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={dialogTab} onChange={(e, newValue) => setDialogTab(newValue)}>
              <Tab icon={<InfoIcon />} label="Basic Info" />
              {(dialogMode === 'edit' || dialogMode === 'view') && selectedSalon && (
                <Tab icon={<ImageIcon />} label="Image Management" />
              )}
            </Tabs>
          </Box>

          {/* Tab Content */}
          {dialogTab === 0 && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Salon Name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  disabled={dialogMode === 'view'}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  disabled={dialogMode === 'view'}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  disabled={dialogMode === 'view'}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Website"
                  value={formData.website}
                  onChange={(e) => handleInputChange('website', e.target.value)}
                  disabled={dialogMode === 'view'}
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Salon Image
                  </Typography>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="salon-image-upload"
                    type="file"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setFormData(prev => ({ ...prev, image: file }));
                      }
                    }}
                    disabled={dialogMode === 'view'}
                  />
                  <label htmlFor="salon-image-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      disabled={dialogMode === 'view'}
                      sx={{ mr: 2 }}
                    >
                      Choose Image
                    </Button>
                  </label>
                  {formData.image && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Selected: {formData.image.name}
                      </Typography>
                      <Box sx={{ mt: 1, maxWidth: 200 }}>
                        <img
                          src={URL.createObjectURL(formData.image)}
                          alt="Preview"
                          style={{
                            width: '100%',
                            height: 'auto',
                            borderRadius: '8px',
                            border: '1px solid #ddd'
                          }}
                        />
                      </Box>
                    </Box>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  disabled={dialogMode === 'view'}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Street Address"
                  value={formData.address.street}
                  onChange={(e) => handleInputChange('address.street', e.target.value)}
                  disabled={dialogMode === 'view'}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="City"
                  value={formData.address.city}
                  onChange={(e) => handleInputChange('address.city', e.target.value)}
                  disabled={dialogMode === 'view'}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="State"
                  value={formData.address.state}
                  onChange={(e) => handleInputChange('address.state', e.target.value)}
                  disabled={dialogMode === 'view'}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Zip Code"
                  value={formData.address.zipCode}
                  onChange={(e) => handleInputChange('address.zipCode', e.target.value)}
                  disabled={dialogMode === 'view'}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={fetchingCurrentLocation ? <CircularProgress size={20} /> : <GpsFixedIcon />}
                    onClick={getCurrentLocation}
                    disabled={dialogMode === 'view' || fetchingCurrentLocation}
                    sx={{ minWidth: 200 }}
                  >
                    {fetchingCurrentLocation ? 'Getting Location...' : 'Get Current Location'}
                  </Button>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Longitude"
                  type="number"
                  value={formData.address.coordinates.coordinates[0]}
                  onChange={(e) => {
                    const newCoords = [...formData.address.coordinates.coordinates];
                    newCoords[0] = parseFloat(e.target.value) || 0;
                    setFormData({
                      ...formData,
                      address: {
                        ...formData.address,
                        coordinates: {
                          ...formData.address.coordinates,
                          coordinates: newCoords
                        }
                      }
                    });
                  }}
                  disabled={dialogMode === 'view'}
                  required
                 />
               </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Latitude"
                  type="number"
                  value={formData.address.coordinates.coordinates[1]}
                  onChange={(e) => {
                    const newCoords = [...formData.address.coordinates.coordinates];
                    newCoords[1] = parseFloat(e.target.value) || 0;
                    setFormData({
                      ...formData,
                      address: {
                        ...formData.address,
                        coordinates: {
                          ...formData.address.coordinates,
                          coordinates: newCoords
                        }
                      }
                    });
                  }}
                  disabled={dialogMode === 'view'}
                  required
                 />
               </Grid>
              <Grid item xs={12} md={6}>
                 <FormControl fullWidth disabled={dialogMode === 'view'}>
                   <InputLabel>Price Range</InputLabel>
                   <Select
                     value={formData.priceRange}
                     label="Price Range"
                     onChange={(e) => handleInputChange('priceRange', e.target.value)}
                   >
                     <MenuItem value="budget">Budget (₹)</MenuItem>
                     <MenuItem value="moderate">Moderate (₹₹)</MenuItem>
                     <MenuItem value="premium">Premium (₹₹₹)</MenuItem>
                     <MenuItem value="luxury">Luxury (₹₹₹₹)</MenuItem>
                   </Select>
                 </FormControl>
               </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isActive}
                        onChange={(e) => handleInputChange('isActive', e.target.checked)}
                        disabled={dialogMode === 'view'}
                      />
                    }
                    label="Active"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isVerified}
                        onChange={(e) => handleInputChange('isVerified', e.target.checked)}
                        disabled={dialogMode === 'view'}
                      />
                    }
                    label="Verified"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.featured}
                        onChange={(e) => handleInputChange('featured', e.target.checked)}
                        disabled={dialogMode === 'view'}
                      />
                    }
                    label="Featured"
                  />
                </Box>
              </Grid>
            </Grid>
          )}

          {/* Image Management Tab */}
          {dialogTab === 1 && selectedSalon && (
            <Box>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Salon ID: {selectedSalon.id || 'No ID found'}
              </Typography>
              <ImageManager 
                salonId={selectedSalon.id} 
                onImagesChange={() => {
                  // Refresh salon data if needed
                  fetchSalons();
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          {dialogMode !== 'view' && (
            <Button onClick={handleSubmit} variant="contained">
              {dialogMode === 'add' ? 'Create' : 'Update'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone. The salon and all associated data will be permanently deleted.
          </Alert>
          <Typography>
            Are you sure you want to delete "{salonToDelete?.name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Service Assignment Dialog */}
      <Dialog 
        open={serviceAssignmentOpen} 
        onClose={handleCloseServiceAssignment} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          Assign Services to {selectedSalonForServices?.name}
        </DialogTitle>
        <DialogContent>
          {servicesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              {/* Currently Assigned Services */}
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  Currently Assigned Services ({assignedServices.length})
                </Typography>
                <Card sx={{ maxHeight: 400, overflow: 'auto' }}>
                  <CardContent>
                    {assignedServices.length === 0 ? (
                      <Typography color="text.secondary" align="center">
                        No services assigned yet
                      </Typography>
                    ) : (
                      assignedServices.map((service) => (
                        <Box
                          key={service.service?._id || service._id}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            p: 1,
                            mb: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1
                          }}
                        >
                          <Box>
                            <Typography variant="subtitle2">
                              {service.service?.name || 'Unknown Service'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {service.service?.category || 'Unknown'} • ₹{service.customPrice || service.service?.price || 0}
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveService(service.service?._id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      ))
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Available Services to Assign */}
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  Available Services ({availableServices.length})
                </Typography>
                <Card sx={{ maxHeight: 400, overflow: 'auto' }}>
                  <CardContent>
                    {availableServices.length === 0 ? (
                      <Typography color="text.secondary" align="center">
                        No unassigned services available
                      </Typography>
                    ) : (
                      availableServices.map((service) => (
                        <Box
                          key={service._id}
                          sx={{
                            p: 1,
                            mb: 1,
                            border: '1px solid',
                            borderColor: selectedServices.includes(service._id) 
                              ? 'primary.main' 
                              : 'divider',
                            borderRadius: 1,
                            backgroundColor: selectedServices.includes(service._id) 
                              ? 'primary.50' 
                              : 'transparent'
                          }}
                        >
                          <Box 
                            sx={{ 
                              display: 'flex',
                              alignItems: 'center',
                              cursor: 'pointer',
                              mb: selectedServices.includes(service._id) ? 1 : 0
                            }}
                            onClick={() => handleServiceSelection(service._id)}
                          >
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="subtitle2">
                                {service.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {service.category} • ₹{service.price} (default)
                              </Typography>
                            </Box>
                            {selectedServices.includes(service._id) && (
                              <Chip
                                label="Selected"
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            )}
                          </Box>
                          {selectedServices.includes(service._id) && (
                            <TextField
                              fullWidth
                              size="small"
                              label="Custom Price (₹)"
                              type="number"
                              placeholder={`Default: ₹${service.price}`}
                              value={customPrices[service._id] || ''}
                              onChange={(e) => handleCustomPriceChange(service._id, e.target.value)}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">₹</InputAdornment>
                              }}
                              helperText="Leave empty to use default price"
                              onClick={(e) => e.stopPropagation()}
                            />
                          )}
                        </Box>
                      ))
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseServiceAssignment}>Cancel</Button>
          <Button 
            onClick={handleAssignServices} 
            variant="contained"
            disabled={selectedServices.length === 0 || servicesLoading}
          >
            Assign Selected Services ({selectedServices.length})
          </Button>
        </DialogActions>
      </Dialog>

      {/* Owner Assignment Dialog */}
      <Dialog 
        open={ownerAssignmentOpen} 
        onClose={handleCloseOwnerAssignment} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          Assign Owner to {selectedSalonForOwner?.name}
        </DialogTitle>
        <DialogContent>
          {selectedSalonForOwner?.owner && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Current Owner: {selectedSalonForOwner.owner}
              <Button 
                size="small" 
                color="error" 
                onClick={handleUnassignOwner}
                sx={{ ml: 2 }}
              >
                Unassign Current Owner
              </Button>
            </Alert>
          )}
          
          <TextField
            fullWidth
            label="Search Users"
            value={ownerSearchTerm}
            onChange={handleOwnerSearchChange}
            placeholder="Search by name or email..."
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          
          <FormControl fullWidth>
            <InputLabel>Select Owner</InputLabel>
            <Select
              value={selectedOwner || ''} // Ensure value is never undefined
              label="Select Owner"
              onChange={(event) => {
                const newValue = event.target.value || ''; // Extract value from event.target.value
                console.log('🎯 Dropdown selection changed:', newValue);
                console.log('🎯 Event object:', event);
                console.log('🎯 Event target value:', event.target.value);
                console.log('🎯 Selected value type:', typeof newValue);
                setSelectedOwner(newValue);
                console.log('🎯 selectedOwner should now be:', newValue);
              }}
              disabled={ownersLoading}
            >
              <MenuItem value="">
                <em>Select an owner</em>
              </MenuItem>
              {availableOwners.map((owner) => {
                console.log('🔍 Rendering owner option:', owner);
                console.log('🔍 Owner ID field:', owner.id);
                console.log('🔍 Owner ID type:', typeof owner.id);
                return (
                  <MenuItem key={owner.id} value={owner.id}>
                    <Box>
                      <Typography variant="subtitle2">
                        {owner.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {owner.email}
                      </Typography>
                    </Box>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          
          {ownersLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseOwnerAssignment}>Cancel</Button>
          <Button 
            onClick={handleAssignOwner} 
            variant="contained"
            disabled={!selectedOwner || ownersLoading}
          >
            Assign Owner
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SalonManagement;